"use client"

import { Check, ChevronDown, FileText, Loader2, Search, X } from "lucide-react"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { FieldShell, FIELD_LABEL, FIELD_UNDERLINE } from "@/components/graphite/field"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useIsMobile } from "@/hooks/use-mobile"
import { useAddCandidate, type AddSource, type PoolRow } from "@/hooks/use-add-candidate"
import { STAGE_ORDER, stageHex, stageTint, TINT } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"

/**
 * Adding someone to a job, from canvas artboards 19a–19d, with 19g–19j on
 * white.
 *
 * "Four sources become four tabs in one sheet. Today the talent pool is a
 * popover hanging off one button while LinkedIn, résumé and manual entry live
 * in a different dialog, and only the pool lets you choose a stage — the
 * LinkedIn path hardcodes longlist. Here the stage is stated once, above the
 * tabs, and governs whichever source you use."
 *
 * Candidates already on this job stay in the list, dimmed and ticked, rather
 * than being silently filtered out.
 */

const SOURCES: { id: AddSource; label: string }[] = [
  { id: "pool", label: "Talent pool" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "resume", label: "Résumé" },
  { id: "manual", label: "By hand" },
]

/** The stages a candidate can be put straight into. */
const ENTRY_STAGES = STAGE_ORDER.filter(
  (stage) => stage !== "hired" && stage !== "rejected",
)

export function AddCandidateSheet({
  jobId,
  jobTitle,
  companyName,
  isOpen,
  onClose,
  initialSource,
}: {
  jobId: string
  jobTitle?: string
  companyName?: string
  isOpen: boolean
  onClose: () => void
  /** Which tab to open on, when something outside asked for a particular one. */
  initialSource?: AddSource
}) {
  const add = useAddCandidate({ jobId, isOpen, onClose, initialSource })
  const isMobile = useIsMobile()

  const subtitle = [jobTitle, companyName].filter(Boolean).join(" @ ")
  /*
    Every tab commits through the stage line, so the label always names where
    they land — only what has to be true first differs by source.
  */
  const commitLabel =
    add.source === "pool" && add.pickedCount > 0
      ? `Add ${add.pickedCount} to ${add.stage}`
      : `Add to ${add.stage}`

  const canCommit = {
    pool: add.pickedCount > 0,
    linkedin: Boolean(add.linkedinUrl.trim()),
    resume: Boolean(add.parsed?.full_name?.trim() && add.parsed?.email?.trim()),
    manual: Boolean(add.manual.full_name.trim() && add.manual.email.trim()),
  }[add.source]

  const commit = {
    pool: add.addPicked,
    linkedin: add.addLinkedin,
    resume: add.addResume,
    manual: add.addManual,
  }[add.source]

  const body = (
    <Body add={add} compact={isMobile} />
  )

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="bottom"
          hideClose
          className="flex h-[92vh] flex-col gap-0 rounded-t-2xl p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex flex-none items-center gap-2 border-b px-2 pb-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 items-center px-3 text-[13px] text-muted-foreground"
            >
              Cancel
            </button>
            <div className="min-w-0 flex-1 text-center">
              <SheetTitle className="truncate text-[14.5px] font-semibold">
                Add a candidate
              </SheetTitle>
              {jobTitle && (
                <div className="truncate font-mono text-[10.5px] text-muted-foreground">
                  {jobTitle}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={commit}
              disabled={!canCommit || add.isSaving}
              className="inline-flex h-11 items-center px-3 text-[13px] font-medium text-brand-ink disabled:opacity-40"
            >
              {add.isSaving
                ? "Adding…"
                : add.source === "pool" && add.pickedCount > 0
                  ? `Add ${add.pickedCount}`
                  : "Add"}
            </button>
          </div>
          {body}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-[620px] flex-col gap-0 overflow-hidden p-0"
      >
        <header className="flex-none border-b px-[26px] pb-[14px] pt-5">
          <DialogTitle className="text-[16px] font-semibold leading-[1.2] tracking-[-0.02em]">
            Add a candidate
          </DialogTitle>
          {subtitle && (
            <p className="mt-2 text-[12.5px] leading-[1.5] text-muted-foreground">
              {subtitle}
            </p>
          )}
        </header>
        {body}
        <footer className="flex flex-none items-center gap-[22px] border-t px-[26px] pb-5 pt-[18px]">
          <button
            type="button"
            onClick={commit}
            disabled={!canCommit || add.isSaving}
            className="inline-flex items-center gap-2 text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {add.isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {commitLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">
            {add.source === "pool"
              ? "every source lands in the same stage"
              : "creates a candidate and a pipeline entry"}
          </span>
        </footer>
      </DialogContent>
    </Dialog>
  )
}

type Add = ReturnType<typeof useAddCandidate>

function Body({ add, compact }: { add: Add; compact: boolean }) {
  const pad = compact ? "px-[18px]" : "px-[26px]"

  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto pt-4", pad, compact ? "pb-10" : "pb-6")}>
      {/*
        19a states the stage once, as a strip: the chosen stage with a chevron,
        and a note that it governs every tab. It is deliberately not a row of
        every stage — the sheet is about who, not about where they land.
      */}
      <div className="mt-3.5 flex items-center gap-3 rounded-md border bg-chrome px-[11px] py-[9px]">
        <span className="text-[11.5px] text-muted-foreground">Adding to</span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Adding to ${add.stage}. Change stage`}
              className="inline-flex items-center gap-[7px] text-[12px] font-medium transition-opacity hover:opacity-80"
            >
              <span
                className="h-[6px] w-[6px] rounded-full"
                style={{ backgroundColor: stageHex(add.stage) }}
              />
              {add.stage}
              <ChevronDown className="h-[13px] w-[13px] text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {ENTRY_STAGES.map((stage) => (
              <DropdownMenuItem
                key={stage}
                onSelect={() => add.setStage(stage)}
                className={cn("text-[12.5px]", stage === add.stage && "font-medium")}
              >
                <span
                  className="mr-2 h-[6px] w-[6px] rounded-full"
                  style={{ backgroundColor: stageHex(stage) }}
                />
                {stage}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="ml-auto hidden font-mono text-[10.5px] text-muted-foreground @sm:inline">
          every source lands in the same stage
        </span>
      </div>

      <div
        role="tablist"
        aria-label="Where the candidate comes from"
        className="mt-4 flex gap-[22px] overflow-x-auto border-b"
      >
        {SOURCES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={entry.id === add.source}
            onClick={() => add.setSource(entry.id)}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-0.5 py-2.5 text-[12.5px] transition-colors",
              entry.id === add.source
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {add.source === "pool" && <PoolTab add={add} compact={compact} />}
      {add.source === "linkedin" && <LinkedinTab add={add} />}
      {add.source === "resume" && <ResumeTab add={add} compact={compact} />}
      {add.source === "manual" && <ManualTab add={add} compact={compact} />}
    </div>
  )
}

function PoolTab({ add, compact }: { add: Add; compact: boolean }) {
  return (
    <>
      <div className="mt-4 flex items-center gap-2 border-b pb-[7px]">
        <Search className="h-[15px] w-[15px] flex-none text-muted-foreground" />
        <input
          value={add.query}
          onChange={(event) => add.setQuery(event.target.value)}
          placeholder="Search the pool by name, role or skill"
          aria-label="Search the talent pool"
          className={cn(FIELD_UNDERLINE, "border-b-0 pb-0 text-[13px]")}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
        {/*
          19a writes "34 of 1,284 match". The pool total has no endpoint that is
          cheap to ask for here, so this states the half that does.
        */}
        <span>
          {add.hasQuery ? `${add.rows.length} match` : "type to search the pool"}
        </span>
        {add.alreadyHere > 0 && <span>{add.alreadyHere} already on this job</span>}
        {add.pickedCount > 0 && (
          <span className="text-brand-ink">{add.pickedCount} picked</span>
        )}
      </div>

      <div className="mt-2">
        {add.isSearching && add.rows.length === 0 ? (
          <p className="flex items-center gap-2 border-t py-3.5 text-[12.5px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Searching the pool…
          </p>
        ) : add.hasQuery && add.rows.length === 0 ? (
          <p className="border-t py-3.5 text-[12.5px] text-muted-foreground">
            Nobody in the pool matches “{add.query.trim()}”.
          </p>
        ) : (
          add.rows.map((row) => (
            <PoolRowView
              key={row.id}
              row={row}
              compact={compact}
              picked={add.picked.has(row.id)}
              onToggle={() => add.toggle(row.id)}
            />
          ))
        )}
      </div>
    </>
  )
}

/** Already on the job: visible, dimmed and ticked, never silently dropped. */
function PoolRowView({
  row,
  compact,
  picked,
  onToggle,
}: {
  row: PoolRow
  compact: boolean
  picked: boolean
  onToggle: () => void
}) {
  const assigned = Boolean(row.assignedStage)

  return (
    <button
      type="button"
      onClick={assigned ? undefined : onToggle}
      disabled={assigned}
      aria-pressed={assigned ? undefined : picked}
      className={cn(
        "flex w-full items-center gap-3 border-t py-3 text-left transition-opacity",
        assigned && "cursor-default opacity-45",
      )}
    >
      <span
        className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-[11.5px] font-medium"
        style={{
          backgroundColor: stageTint(stageHex(row.assignedStage ?? "longlist"), TINT.pill),
          color: stageHex(row.assignedStage ?? "longlist"),
        }}
      >
        {row.initials}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-medium leading-[1.3]">{row.name}</span>
          {row.source && !compact && (
            <span className="flex-none font-mono text-[10px] text-muted-foreground">
              {row.source}
            </span>
          )}
        </span>
        <span className="mt-[3px] block truncate text-[11.5px] leading-[1.35] text-muted-foreground">
          {row.role}
        </span>
        <span className="mt-[3px] block font-mono text-[10.5px] text-muted-foreground">
          {row.assignedStage ? `already in ${row.assignedStage}` : row.standing}
        </span>
      </span>

      <span
        className={cn(
          "flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border",
          picked && "border-transparent bg-primary text-primary-foreground",
          assigned && "border-transparent",
        )}
      >
        {(picked || assigned) && <Check className="h-3 w-3" />}
      </span>
    </button>
  )
}

/**
 * 19c minus its preview: there is no parse-only endpoint for a profile, so the
 * candidate is still created by the paste. The stage choice — the other half of
 * that artboard, and the one the shipped path never offered — is real.
 */
function LinkedinTab({ add }: { add: Add }) {
  return (
    <div className="pt-5">
      <FieldShell label="Profile URL">
        <input
          value={add.linkedinUrl}
          onChange={(event) => add.setLinkedinUrl(event.target.value)}
          placeholder="linkedin.com/in/…"
          aria-label="Profile URL"
          inputMode="url"
          className={cn(FIELD_UNDERLINE, "pb-[7px] text-[13px]")}
        />
      </FieldShell>

      <p className="mt-3 text-[11.5px] leading-[1.6] text-muted-foreground">
        Paste a profile and the record is built from it: one candidate, and one
        pipeline entry in the stage above. Contact details are a separate step — the
        profile alone does not give an email.
      </p>
    </div>
  )
}

/** A read-only line from a parsed record: what it is, and what it says. */
function ReadRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-baseline gap-3 border-t py-[9px]">
      <span className="w-[86px] flex-none font-mono text-[10.5px] text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-[12.5px] leading-[1.4]">{value || "—"}</span>
    </div>
  )
}

/**
 * Résumé, by 19c's rule rather than its picture: "a pasted profile is read
 * before it is written… so you can see what you are about to create."
 *
 * LinkedIn cannot honour that — there is no parse-only endpoint for a profile —
 * but a résumé can: `POST /candidates/parse-resume` reads the file and saves
 * nothing, so the parsed record really is shown before anything is written.
 */
function ResumeTab({ add, compact }: { add: Add; compact: boolean }) {
  const parsed = add.parsed

  return (
    <div className="pt-5">
      {!add.resumeFile ? (
        <>
          <label className="flex cursor-pointer items-center gap-3 border-b pb-[9px]">
            <FileText className="h-[15px] w-[15px] flex-none text-muted-foreground" />
            <span className="text-[13px] text-brand-ink">Choose a résumé</span>
            <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
              pdf, doc, docx
            </span>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              aria-label="Choose a résumé"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void add.pickResume(file)
                event.target.value = ""
              }}
              className="sr-only"
            />
          </label>
          <p className="mt-3 text-[11.5px] leading-[1.6] text-muted-foreground">
            The file is read first and nothing is saved until you add them. The résumé
            itself is stored on the candidate.
          </p>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 border-b pb-[9px]">
            <FileText className="h-[15px] w-[15px] flex-none text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-[13px]">{add.resumeFile.name}</span>
            <button
              type="button"
              onClick={add.clearResume}
              aria-label="Remove this résumé"
              className="flex-none text-muted-foreground transition-colors hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {add.isParsing ? (
            <p className="mt-4 flex items-center gap-2 text-[12.5px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Reading the résumé…
            </p>
          ) : parsed ? (
            <div className="mt-5">
              <div className="flex items-baseline gap-3.5">
                <div className={FIELD_LABEL}>Read from the résumé</div>
                <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
                  not saved yet
                </span>
              </div>

              <div className="mt-3">
                <EditRow
                  label="Name"
                  value={parsed.full_name}
                  onChange={(value) => add.setParsedField("full_name", value)}
                />
                <EditRow
                  label="Email"
                  value={parsed.email}
                  onChange={(value) => add.setParsedField("email", value)}
                />
                <ReadRow label="Headline" value={parsed.headline} />
                <ReadRow label="Location" value={parsed.location} />
                <ReadRow
                  label="Experience"
                  value={
                    parsed.experiences.length
                      ? `${parsed.experiences.length} ${
                          parsed.experiences.length === 1 ? "role" : "roles"
                        }`
                      : undefined
                  }
                />
                {!compact && <ReadRow label="Skills" value={parsed.skills.join(", ")} />}
              </div>

              <p className="mt-3 text-[11.5px] leading-[1.6] text-muted-foreground">
                Name and email are what the candidate is created from, so they can be
                corrected here. The rest comes from the file.
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

/** An underline field on a hairline row, so a form reads as a list of facts. */
function EditRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value?: string | null
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <div className="flex items-baseline gap-3 border-t py-[7px]">
      <span className="w-[86px] flex-none font-mono text-[10.5px] text-muted-foreground">
        {label}
      </span>
      <input
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className={cn(FIELD_UNDERLINE, "border-b-0 pb-0 text-[12.5px] leading-[1.4]")}
      />
    </div>
  )
}

/**
 * By hand. The same rows as the résumé readout, all of them writable — one
 * list of facts rather than a boxed form with its own headings.
 */
function ManualTab({ add, compact }: { add: Add; compact: boolean }) {
  const draft = add.manual

  return (
    <div className="pt-5">
      <div className={FIELD_LABEL}>The candidate</div>
      <div className="mt-3">
        <EditRow
          label="Name"
          value={draft.full_name}
          placeholder="Required"
          onChange={(value) => add.setManualField("full_name", value)}
        />
        <EditRow
          label="Email"
          value={draft.email}
          placeholder="Required"
          onChange={(value) => add.setManualField("email", value)}
        />
        <EditRow
          label="Headline"
          value={draft.headline}
          onChange={(value) => add.setManualField("headline", value)}
        />
        <EditRow
          label="Location"
          value={draft.location}
          onChange={(value) => add.setManualField("location", value)}
        />
        <EditRow
          label="Phone"
          value={draft.phone_number}
          onChange={(value) => add.setManualField("phone_number", value)}
        />
        {!compact && (
          <EditRow
            label="LinkedIn"
            value={draft.linkedin_profile_url}
            onChange={(value) => add.setManualField("linkedin_profile_url", value)}
          />
        )}
      </div>

      <p className="mt-3 text-[11.5px] leading-[1.6] text-muted-foreground">
        A name and an email are enough to create the candidate. Everything else can be
        filled in on their record later.
      </p>
    </div>
  )
}
